import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import React, { useState } from 'react';

/**
 * SearchFiltersCard Component
 * Collapsible card for People Search filters
 *
 * @param {Object} props
 * @param {Object} props.filters - Current filter values
 * @param {Function} props.onFilterChange - Handler for filter changes
 * @param {Function} props.onSearch - Handler for search action
 * @param {Function} props.onClear - Handler for clear action
 * @param {boolean} props.isLoading - Loading state
 */
export function SearchFiltersCard({
  filters = {},
  onFilterChange,
  onSearch,
  onClear,
  isLoading = false
}) {
  const [isExpanded, setIsExpanded] = useState(true);

  const handleInputChange = (field, value) => {
    if (onFilterChange) {
      onFilterChange({ ...filters, [field]: value });
    }
  };

  const handleClear = () => {
    if (onClear) {
      onClear();
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 animate-fade-in">
      {/* Header */}
      <div
        className="flex items-center justify-between mb-6 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <Search className="w-6 h-6 text-blue-600" />
          <h2 className="text-xl font-bold text-gray-900">Search Filters</h2>
        </div>
        <button className="p-2 hover:bg-gray-100 rounded-lg transition">
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-gray-600 transition-transform duration-200" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-600 transition-transform duration-200" />
          )}
        </button>
      </div>

      {/* Filter Form */}
      {isExpanded && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Job Titles */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Titles <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Software Engineer, Product Manager, Data Scientist"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.jobTitles || ''}
                onChange={(e) => handleInputChange('jobTitles', e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter multiple titles separated by commas
              </p>
            </div>

            {/* Locations */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Locations
              </label>
              <input
                type="text"
                placeholder="San Francisco, New York, Remote"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.locations || ''}
                onChange={(e) => handleInputChange('locations', e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter multiple locations separated by commas
              </p>
            </div>

            {/* Keywords */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Keywords
              </label>
              <input
                type="text"
                placeholder="React, Python, AWS, Machine Learning"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.keywords || ''}
                onChange={(e) => handleInputChange('keywords', e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter specific skills or keywords to search for
              </p>
            </div>

            {/* Email Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Status
              </label>
              <select
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.emailStatus || 'verified'}
                onChange={(e) => handleInputChange('emailStatus', e.target.value)}
              >
                <option value="verified">Verified Emails Only</option>
                <option value="unverified">Unverified Emails Only</option>
                <option value="all">All Emails</option>
              </select>
            </div>

            {/* Results per Page */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Results per Page
              </label>
              <select
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.perPage || '25'}
                onChange={(e) => handleInputChange('perPage', e.target.value)}
              >
                <option value="10">10 results</option>
                <option value="25">25 results</option>
                <option value="50">50 results</option>
                <option value="100">100 results</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                More results may take longer to load
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center justify-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={onSearch}
              disabled={isLoading || !filters.jobTitles}
            >
              <Search className="w-5 h-5" />
              {isLoading ? 'Searching...' : 'Search Candidates'}
            </button>
            <button
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleClear}
              disabled={isLoading}
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}



